import { useEffect, useState } from "react";
import { Button, Input, Label, Modal, ModalFooter, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { Contact } from "@/lib/types";

/**
 * Intentionally tiny: first name, last name, email, phone. Everything
 * else (address, socials, custom fields, lifecycle, do-not-contact, …)
 * is edited inline on the contact-detail page once the row exists.
 *
 * Keeping the create flow this short means the user can capture a new
 * contact in seconds instead of staring at a dozen fields they don't
 * need to fill in yet.
 */
export function NewContactDialog({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: Contact) => void;
}) {
  const toast = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setFirstName(""); setLastName(""); setEmail(""); setPhone(""); }
  }, [open]);

  const canSubmit = (firstName.trim() || lastName.trim() || email.trim() || phone.trim());

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Add at least a name, email, or phone");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post<{ contact: Contact }>("/contacts", {
        first_name: firstName.trim() || null,
        last_name:  lastName.trim() || null,
        email:      email.trim() || null,
        phone:      phone.trim() || null,
      });
      toast.success("Contact created");
      onCreated(r.contact);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to create contact");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New contact" size="sm">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        </div>
        <div>
          <Label>Phone</Label>
          <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1…" />
        </div>
        <p className="text-[11px] text-zinc-500">
          You'll be able to add address, socials, tags, and custom fields right after the contact is created.
        </p>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!canSubmit}>Create</Button>
      </ModalFooter>
    </Modal>
  );
}
